import React, {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {
    CURRENCIES_WITH_INTERVIEW_PROMO_PRICES,
    CurrenciesWithInterviewPromoPrices,
    Currency,
    StatData
} from 'client/common/types';
import {formatPriceValue} from 'client/common/utils/format';
import {PaymentType} from 'client/common/components/card-payment-form/types';
import {
    Button,
    Icon,
    Radio,
    Radiobox,
    Spin,
    Textinput,
    Tumbler
} from 'client/common/components/lego';

import '../unlock-interview-popup.css';
import I18N from 'assets/i18n/brandlift-project';
import b_ from 'b_';
import {FormActionType} from 'client/common/components/unlock-interview-popup/unlock-interview-popup';
import {useIsAvailableUserCurrency, useIsYandexPublisher, useUserCurrency} from 'client/common/hooks/user';
import {useInterviewBalance, useTopUpInterviewsFromWallet} from 'client/common/hooks/payment';
import declOfNum from 'client/common/utils/decl-of-num';
import {arrayIncludes} from 'ts-extras';

const i18n = I18N.surveyStats.unlockInterviewPopup;
const i18nSurveySettings = I18N.common.surveySettings;

const interviewAmountAvailable = declOfNum(i18n.interviewAmountAvailable);

const amountFromInterviewsWallet = declOfNum(i18n.amountFromInterviewsWallet);

const b = b_.with('unlock-interview-popup');

type clickPayInterviews = (callback: () => Promise<any>) => Promise<void>;

interface UnlockInterviewViewProps {
    className?: string;
    customTitle?: string;
    customSubtitle?: string;
    formActionDefault?: FormActionType;
    formAction: FormActionType;
    isFinishedInteviewsActivated: boolean;
    setIsFinishedInteviewsActivated: Dispatch<SetStateAction<boolean>>;
    setFormAction: Dispatch<SetStateAction<FormActionType>>;
    handleChangeInterviewCount: (e: React.ChangeEvent<HTMLInputElement>) => void;
    count?: number;
    maxCount?: number;
    costReady: boolean;
    costData?:
        | false
        | {
              vat: boolean;
              price: {
                  value: number;
                  currency: Currency;
              };
          };
    ableToPay?: boolean;
    disabled: boolean;
    payAs: PaymentType;
    setPayAs: Dispatch<SetStateAction<PaymentType>>;
    handleClickBillPay: () => Promise<void>;
    handlePayingInterviewFromWallet: clickPayInterviews;
    interviewTotal?: number;
    balance?: {
        balanceWithoutVat?: number;
        balanceWithVat?: number;
        currency?: Currency | null;
    };
    slug?: string;
    handleClickPayFromInterviewsWallet: () => Promise<void>;
    stat?: StatData;
    lockedUnfinishedInterviewsCount?: number;
    lockedFinishedInteviewsCount?: number;
    availablePaymentMethods: string[];
}

const promoPrices: Array<{
    title: string;
    value: {
        [key in CurrenciesWithInterviewPromoPrices]: number;
    };
}> = [
    {
        // eslint-disable-next-line no-cyrillic-string/no-cyrillic-string
        title: 'до 1000',
        value: {
            RUB: 3,
            KZT: 20,
            BYN: 0.15
        }
    },
    {
        title: '1000+',
        value: {
            RUB: 1.5,
            KZT: 15,
            BYN: 0.07
        }
    },
    {
        title: '5000+',
        value: {
            RUB: 1.2,
            KZT: 12,
            BYN: 0.06
        }
    },
    {
        title: '100 000+',
        value: {
            RUB: 0.99,
            KZT: 10,
            BYN: 0.05
        }
    },
    {
        title: '200 000+',
        value: {
            RUB: 0.89,
            KZT: 8.5,
            BYN: 0.04
        }
    },
    {
        title: '300 000',
        value: {
            RUB: 0.79,
            KZT: 7,
            BYN: 0.03
        }
    }
];

// eslint-disable-next-line complexity
export default function UnlockInterviewView(props: UnlockInterviewViewProps) {
    const {
        className,
        customTitle,
        ableToPay,
        costData,
        costReady,
        count = 1,
        formAction,
        formActionDefault,
        setFormAction,
        disabled,
        customSubtitle,
        payAs,
        setPayAs,
        interviewTotal,
        balance,
        slug,
        handleClickBillPay,
        handlePayingInterviewFromWallet,
        handleClickPayFromInterviewsWallet,
        handleChangeInterviewCount,
        isFinishedInteviewsActivated,
        setIsFinishedInteviewsActivated,
        stat,
        lockedUnfinishedInterviewsCount = 0,
        lockedFinishedInteviewsCount = 0,
        availablePaymentMethods
    } = props;

    const [interviewWalletError, setInterviewWalletError] = useState('');

    const userIsYandexPublisher = useIsYandexPublisher();
    const currency = useUserCurrency();

    const displayPromoPrices =
        arrayIncludes(CURRENCIES_WITH_INTERVIEW_PROMO_PRICES, currency) && !userIsYandexPublisher;

    const interviewBalance = useInterviewBalance();

    let disableBuyFromInterviewWallet = true;

    if (interviewBalance.data) {
        disableBuyFromInterviewWallet =
            count > Number(interviewTotal) || count > interviewBalance.data.total || !count;
    }

    useEffect(() => {
        let notEnoughInterviewsInWallet;

        if (interviewBalance.data) {
            notEnoughInterviewsInWallet = interviewTotal && count > interviewBalance.data?.total;
        }

        const notEnoughInterviewsToOpen = count > Number(interviewTotal) && Number(interviewTotal);

        if (notEnoughInterviewsInWallet && interviewBalance.data) {
            setInterviewWalletError(
                i18n.notEnoughtInterviewsToOpen
                    .replace('%s', String(count))
                    .replace('%n', String(count - interviewBalance.data.total))
            );
        }

        if (notEnoughInterviewsToOpen) {
            setInterviewWalletError(i18n.notEnoughInterviewsToOpen);
        }
    }, [interviewTotal, count, isFinishedInteviewsActivated, interviewBalance.data]);

    return (
        <div className={className ? `${b()} ${className}` : b()}>
            <h3 className={b('title')}>{customTitle ? customTitle : i18n.title}</h3>
            <span className={b('interview-subtitle')}>
                {customSubtitle ? customSubtitle : i18n.buyInterviewSubtitle}
            </span>

            {!formActionDefault && (
                <InterviewActionChoices
                    formAction={formAction}
                    interviewTotal={interviewBalance.data?.total}
                    setFormAction={setFormAction}
                    balance={balance}
                    setIsFinishedInteviewsActivated={setIsFinishedInteviewsActivated}
                    isFinishedInteviewsActivated={isFinishedInteviewsActivated}
                    stat={stat}
                />
            )}

            {displayPromoPrices && formAction === 'buy' && <PromoPrices currency={currency} />}

            <div className={b('interview-label', {margin: 'mt36'})}>{i18n.amountLabel}</div>
            <div className={b('interview-input-container')}>
                <Textinput
                    size='m'
                    type='number'
                    view='dl'
                    className={b('interview-input')}
                    onChange={handleChangeInterviewCount}
                    value={count}
                    state={count ? undefined : 'error'}
                />
            </div>

            <div className={b('interview-subtitle-wrapper', {state: formAction})}>
                {costReady && costData ? (
                    <InterviewPopupSubtitle
                        vat={costData.vat}
                        formAction={formAction}
                        count={count}
                        interviewWalletError={interviewWalletError}
                        disableBuyFromInterviewWallet={disableBuyFromInterviewWallet}
                        price={{
                            value: costData.price.value,
                            currency: costData.price.currency
                        }}
                        ableToPay={ableToPay}
                        setIsFinishedInteviewsActivated={setIsFinishedInteviewsActivated}
                        lockedUnfinishedInterviewsCount={lockedUnfinishedInterviewsCount}
                        lockedFinishedInteviewsCount={lockedFinishedInteviewsCount}
                        isFinishedInteviewsActivated={isFinishedInteviewsActivated}
                    />
                ) : (
                    <Spin size='s' view='default' progress />
                )}
            </div>

            <FooterActionButtons
                formAction={formAction}
                ableToPay={ableToPay}
                count={count}
                disabled={disabled}
                disableBuyFromInterviewWallet={Boolean(disableBuyFromInterviewWallet)}
                handleClickPayFromInterviewsWallet={handleClickPayFromInterviewsWallet}
                isInterviewCostLoaded={costReady}
                payAs={payAs}
                setPayAs={setPayAs}
                slug={slug}
                handleClickBillPay={handleClickBillPay}
                handlePayingInterviewFromWallet={handlePayingInterviewFromWallet}
                availablePaymentMethods={availablePaymentMethods}
            />
        </div>
    );
}

interface InterviewPopupSubtitleProps {
    vat: boolean;
    formAction: FormActionType;
    ableToPay?: boolean;
    price: {
        value: number;
        currency: Currency;
    };
    count: number;
    interviewWalletError: string;
    disableBuyFromInterviewWallet?: number | boolean;
    setIsFinishedInteviewsActivated: Dispatch<SetStateAction<boolean>>;
    lockedUnfinishedInterviewsCount: number;
    lockedFinishedInteviewsCount: number;
    isFinishedInteviewsActivated: boolean | undefined;
}

function InterviewPopupSubtitle(props: InterviewPopupSubtitleProps) {
    const {
        vat,
        price,
        formAction,
        count,
        disableBuyFromInterviewWallet,
        interviewWalletError,
        setIsFinishedInteviewsActivated,
        lockedUnfinishedInterviewsCount,
        lockedFinishedInteviewsCount,
        isFinishedInteviewsActivated
    } = props;

    const withOrWithoutVat = vat ? i18nSurveySettings.withVat : i18nSurveySettings.withoutVat;

    const year = new Date().getFullYear();

    const activateUnfinishedSurveys = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        if (setIsFinishedInteviewsActivated) {
            setIsFinishedInteviewsActivated(true);
        }
    };
    const isErrorVisible = () => {
        if (!isFinishedInteviewsActivated) {
            if (lockedUnfinishedInterviewsCount > 0 && lockedFinishedInteviewsCount === 0) {
                return true;
            }
        }

        return false;
    };

    return (
        <div
            className={b('interview-subtitle', {
                state: 'default'
            })}
        >
            {formAction === 'buy' ? (
                <>
                    <span>
                        {i18n.payIndividualBtnText.replace(
                            '%s',
                            `${formatPriceValue({
                                value: price.value,
                                currency: price.currency
                            })} ${withOrWithoutVat}`
                        )}
                    </span>

                    <div>{i18n.interviewsExpire.replace('%s', String(year))}</div>
                </>
            ) : (
                <div>
                    {disableBuyFromInterviewWallet ? (
                        <span className={b('interview-subtitle', {state: 'error'})}>
                            {interviewWalletError}
                        </span>
                    ) : (
                        <span>
                            {amountFromInterviewsWallet(count).replace('%s', String(count))}
                        </span>
                    )}
                    {isErrorVisible() && (
                        <span>
                            {i18n.noFinishedInterviewsPart1}{' '}
                            <a
                                onClick={activateUnfinishedSurveys}
                                className={b('interview-subtitle_link')}
                                href='#'
                            >
                                {i18n.noFinishedInterviewsPart2}
                            </a>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

interface InterviewPaymentChoicesProps {
    ableToPay?: boolean;
    disabled: boolean;
    payAs: PaymentType;
    setPayAs: Dispatch<SetStateAction<PaymentType>>;
    handleClickBillPay: () => Promise<void>;
    handlePayingInterviewFromWallet: clickPayInterviews;
    slug?: string;
    count: number;
    availablePaymentMethods: string[];
}

function InterviewPaymentChoices(props: InterviewPaymentChoicesProps) {
    const {
        payAs,
        setPayAs,
        handleClickBillPay,
        disabled,
        handlePayingInterviewFromWallet,
        ableToPay,
        count,
        availablePaymentMethods
    } = props;

    return (
        <>
            {availablePaymentMethods.length > 1 && (
                <div className={b('payment-choices')}>
                    <h5 className={b('interview-label', {margin: 'mb12'})}>{i18n.paymentOption}</h5>

                    <div className={b('payment-choices-list')}>
                        <Radiobox
                            view='default'
                            size='m'
                            value={payAs}
                            onChange={event => setPayAs(event.target.value as PaymentType)}
                            className={b('radio-box')}
                        >
                            {availablePaymentMethods.includes('individual') && (
                                <div
                                    className={b('payment-choice')}
                                    onClick={() => setPayAs('individual')}
                                >
                                    <Radio value='individual' />
                                    <div className={b('interview-label')}>
                                        {i18n.paymentNatural}
                                    </div>
                                    <span className={b('interview-subtitle', {size: 'small'})}>
                                        {i18n.paymentNaturalDescription}
                                    </span>
                                </div>
                            )}

                            <div className={b('payment-choice')} onClick={() => setPayAs('entity')}>
                                <Radio value='entity' />
                                <div className={b('interview-label')}>{i18n.paymentJuridical}</div>

                                <span className={b('interview-subtitle', {size: 'small'})}>
                                    {i18n.paymentJuridicalDescription}
                                </span>
                            </div>
                        </Radiobox>
                    </div>
                </div>
            )}

            <div className={b('action-panel')}>
                <Button
                    view='dl-default'
                    size='m'
                    iconLeft={(className: string) => (
                        <Icon size='s' className={className} type='bill' />
                    )}
                    onClick={handleClickBillPay}
                    disabled={disabled}
                >
                    <span>{payAs === 'individual' ? i18n.payByCard : i18n.payTheBill}</span>
                </Button>

                <ButtonTopUpInterviewsWallet
                    count={count}
                    handleClick={handlePayingInterviewFromWallet}
                    disabled={disabled || !ableToPay}
                />
            </div>
        </>
    );
}

interface ButtonTopUpInterviewsWalletProps {
    count: number;
    disabled: boolean;
    handleClick: clickPayInterviews;
}

function ButtonTopUpInterviewsWallet(props: ButtonTopUpInterviewsWalletProps) {
    const {count, disabled, handleClick} = props;
    const topUpInterviewsFromWallet = useTopUpInterviewsFromWallet(count);

    return (
        <Button
            view='dl-action'
            size='m'
            iconLeft={(className: string) => <Icon size='s' className={className} type='wallet' />}
            onClick={async () => handleClick(topUpInterviewsFromWallet)}
            disabled={disabled}
        >
            {i18n.payFromWallet}
        </Button>
    );
}

interface InterviewActionChoicesProps {
    formAction: FormActionType;
    setFormAction: Dispatch<SetStateAction<FormActionType>>;
    interviewTotal?: number;
    balance?: {
        balanceWithoutVat?: number;
        balanceWithVat?: number;
        currency?: Currency | null;
    };
    isFinishedInteviewsActivated: boolean;
    setIsFinishedInteviewsActivated: Dispatch<SetStateAction<boolean>>;
    stat?: StatData;
}

function InterviewActionChoices(props: InterviewActionChoicesProps) {
    const {
        formAction,
        setFormAction,
        interviewTotal,
        balance,
        isFinishedInteviewsActivated,
        setIsFinishedInteviewsActivated,
        stat
    } = props;

    const isAvailableCurrency = useIsAvailableUserCurrency();

    return (
        <div>
            <Radiobox
                view='default'
                size='m'
                value={formAction}
                onChange={event => setFormAction(event.target.value as FormActionType)}
                className={b('form-action-radio-wrapper')}
            >
                <Radio value='useExisting' disabled={!interviewTotal}>
                    <div>
                        <div className={b('action-radio-title')}>{i18n.payWithInterviews}</div>
                        <div className={b('action-radio-subtitle')}>
                            {interviewTotal
                                ? interviewAmountAvailable(interviewTotal || 0).replace(
                                      '%s',
                                      String(interviewTotal || 0)
                                  )
                                : i18n.zeroInterviewsWarning}
                        </div>
                    </div>
                </Radio>
                {isAvailableCurrency && (
                    <Radio value='buy'>
                        <div>
                            <div className={b('action-radio-title')}>{i18n.buyInterviews}</div>
                            <div className={b('action-radio-subtitle')}>
                                {balance?.currency &&
                                    i18n.walletAvailable.replace(
                                        '%s',
                                        formatPriceValue({
                                            value: balance.balanceWithoutVat || balance.balanceWithVat,
                                            currency: balance.currency
                                        })
                                    )}
                            </div>
                        </div>
                    </Radio>
                )}
            </Radiobox>
            <div className={b('tumbler-wrapper')}>
                <Tumbler
                    className={b('action-tumbler')}
                    checked={isFinishedInteviewsActivated}
                    onChange={() => setIsFinishedInteviewsActivated(!isFinishedInteviewsActivated)}
                    size='m'
                    view='dl'
                />
                <div>
                    <span className={b('action-radio-title')}>{i18n.showUnfinishedTitle}</span>
                    <br />
                    <span className={b('action-radio-subtitle')}>
                        {i18n.showUnfinishedDescription
                            .replace('%s', String(stat?.started_events))
                            .replace('%s', String(stat?.finished_events))
                            .replace('%s', String(stat!.started_events - stat!.finished_events))}
                    </span>
                    <br />
                    <span className={b('action-radio-subtitle')}>
                        {i18n.showUnfinishedDescriptionPart2}
                    </span>
                </div>
            </div>
        </div>
    );
}

interface PromoPricesProps {
    currency: CurrenciesWithInterviewPromoPrices;
}

function PromoPrices(props: PromoPricesProps) {
    return (
        <div className={b('promo-prices')}>
            <h6 className={b('promo-prices-title')}>{i18n.promoBlocksTitle}</h6>
            <ul className={b('promo-prices-list')}>
                {promoPrices.map(price => (
                    <li key={price.title} className={b('promo-price')}>
                        <div className={b('promo-price-title')}>{price.title}</div>
                        <span className={b('promo-price-unit')}>
                            {formatPriceValue({
                                value: price.value[props.currency],
                                currency: props.currency
                            })}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

interface FormActionButtonsProps {
    formAction: FormActionType;
    ableToPay?: boolean;
    disabled: boolean;
    isInterviewCostLoaded?: boolean;
    payAs: PaymentType;
    setPayAs: Dispatch<SetStateAction<PaymentType>>;
    handleClickBillPay: () => Promise<void>;
    handlePayingInterviewFromWallet: clickPayInterviews;
    handleClickPayFromInterviewsWallet: () => Promise<void>;
    disableBuyFromInterviewWallet: boolean;
    slug?: string;
    count: number;
    availablePaymentMethods: string[];
}

function FooterActionButtons(props: FormActionButtonsProps) {
    const {
        formAction,
        ableToPay,
        isInterviewCostLoaded,
        disabled,
        payAs,
        setPayAs,
        slug,
        count,
        disableBuyFromInterviewWallet,
        handleClickBillPay,
        handleClickPayFromInterviewsWallet,
        handlePayingInterviewFromWallet,
        availablePaymentMethods
    } = props;

    return formAction === 'buy' ? (
        <InterviewPaymentChoices
            ableToPay={ableToPay}
            disabled={disabled || !isInterviewCostLoaded}
            slug={slug}
            count={count}
            payAs={payAs}
            setPayAs={setPayAs}
            handleClickBillPay={handleClickBillPay}
            handlePayingInterviewFromWallet={handlePayingInterviewFromWallet}
            availablePaymentMethods={availablePaymentMethods}
        />
    ) : (
        <Button
            view='dl-action'
            size='m'
            className={b('interview-form-action-btn')}
            onClick={handleClickPayFromInterviewsWallet}
            disabled={disabled || disableBuyFromInterviewWallet}
        >
            {i18n.payFromWalletInterview}
        </Button>
    );
}
